import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { translations, Language } from "./translations";

// Define the shape of the context value
interface LanguageContextProps {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

// Create the context with default values
const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

// Language provider component
export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // The chosen language is remembered across visits. Guarded so it is safe
  // during react-snap prerendering and when storage is blocked.
  const [language, setLanguage] = useState<Language>(() => {
    if (typeof window === 'undefined') return 'en';
    try {
      const stored = window.localStorage.getItem('lang');
      return stored === 'en' || stored === 'nl' ? stored : 'en';
    } catch {
      return 'en';
    }
  });

  useEffect(() => {
    document.documentElement.lang = language;
    try {
      window.localStorage.setItem('lang', language);
    } catch {
      // Storage unavailable (private mode, blocked site data): ignore.
    }
  }, [language]);

  // Translation function
  const t = (key: string): string => {
    const keys = key.split(".");
    let result: any = translations[language];

    keys.forEach((k) => {
      result = result?.[k];
    });

    return result || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
